import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, inject, Input, ViewChild } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatRippleModule } from '@angular/material/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { AcknowledgeFormComponent } from 'src/app/core/acknowledge-form/acknowledge-form.component';
import { CurrencyComponent } from 'src/app/core/currency/currency.component';
import { SigninSignupComponent } from 'src/app/core/signin-signup/signin-signup.component';
import { IFundraiser } from '@models/fundraiser.model';
import { IUser } from '@models/user.model';
import { DonorCountFormatPipe } from '@pipes/donor-count-format/donor-count-format.pipe';
import { NameInitialsPipe } from '@pipes/name-initials/name-initials.pipe';
import { ApiService } from '@services/api/api.service';
import { UtilService } from '@services/util/util.service';
import { VariablesService } from '@services/variables/variables.service';
import { API_ENDTPOINTS } from '@models/api-endpoints';

@Component({
  selector: 'app-supporters',
  standalone: true,
  imports: [
    NgIf, NgFor, NgTemplateOutlet,
    MatIconModule, MatRippleModule,
    NameInitialsPipe, DonorCountFormatPipe,
    SigninSignupComponent, CurrencyComponent
  ],
  templateUrl: './supporters.component.html',
  styleUrls: ['./supporters.component.scss']
})
export class SupportersComponent {

  private readonly api = inject(ApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  private readonly sheet = inject(MatBottomSheet);
  private readonly util = inject(UtilService);
  readonly vars = inject(VariablesService);

  @Input() type: 'inlineDialog' | 'onpage' = 'inlineDialog';
  @Input() loginMandate = true;
  @Input() fundraiser: IFundraiser;
  @Input() myDonations: any[] = [];

  @ViewChild('viewSupportersTemp', { static: true }) viewSupportersTemp;

  userData: IUser = this.util.getUserData();
  isMobile = this.vars.isMobile;
  supporterImg;
  isLoggedIn = false;
  loginPopupState = false;
  loading = false;
  dialogRef: MatDialogRef<any>;
  sheetRef: MatBottomSheetRef<any>;

  pageData?: { current_page: number; from: number; to: number; next_page_url?: string; prev_page_url?: string; };
  visibleSupporters: any[] = [];
  supportersCache = new Map<number, { supporters: any[]; from: number; to: number; next_page_url?: string; prev_page_url?: string; }>();
  skeletons = Array(5);
  unauthoriseDonor = false;

  dummyVisibleSupporters = [
    { name: 'ABCD', iso_currency: 'INR', donated_amount_local: 1000 },
    { name: 'EFGH', iso_currency: 'INR', donated_amount_local: 1000 }
  ];

  subs: Subscription[] = [];

  ngOnInit() {
    this.skeletons = Array(this.fundraiser?.raised?.backers < 5 ? this.fundraiser?.raised?.backers : 5);
    this.getSupportersImages();
    this.subs.push(this.vars.isLoggedIn$.subscribe({
      next: (res) => {
        this.isLoggedIn = res;
        if (this.type === 'onpage' && !this.visibleSupporters.length && (this.isLoggedIn || !this.loginMandate) && !this.loading) {
          this.getSupporters();
        }
      }
    }));
    if (this.type === 'onpage' && !this.visibleSupporters.length && (this.vars.isLoggedIn$.getValue() || !this.loginMandate)) {
      this.getSupporters();
    }
  }

  getSupportersImages() {
    this.supporterImg = `/assets/images/random_supporters/${Math.floor(Math.random() * 10) + 1}.png`;
  }

  openViewSupporters() {
    if (!this.isLoggedIn && this.loginMandate) {
      this.openLoginPopup();
      return;
    }

    if (this.type === 'inlineDialog') {
      if (this.isMobile) {
        this.openSheet();
      } else {
        this.openDialog();
      }
    }
    this.getSupporters();
  }

  openDialog() {
    this.dialogRef = this.dialog.open(this.viewSupportersTemp, {
      width: '512px',
      maxWidth: '80vw',
      maxHeight: '80vh',
      panelClass: ['all-suppoters-dialog']
    });
  }

  openSheet() {
    this.sheetRef = this.sheet.open(this.viewSupportersTemp, {
      panelClass: ['all-suppoters-sheet']
    });
  }

  openLoginPopup() {
    this.loginPopupState = true;
  }

  signinClosed(event) {
    this.loginPopupState = false;
    if (this.isLoggedIn) this.openViewSupporters();
  }

  closeSupporter() {
    this.dialogRef?.close();
    this.sheetRef?.dismiss();
  }

  getSupporters(page: number = 1, orderby: string = 'id', sortby: 'asc' | 'desc' = 'desc', limit: number = 5) {
    if (this.myDonations?.length > 0) this.unauthoriseDonor = false;
    if (this.loading) return;
    const cachedPage = this.supportersCache.get(page);
    if (cachedPage) {
      this.visibleSupporters = cachedPage.supporters;
      this.pageData = {
        ...this.pageData,
        current_page: page,
        from: cachedPage.from,
        to: cachedPage.to,
        next_page_url: cachedPage.next_page_url,
        prev_page_url: cachedPage.prev_page_url
      } as any;
      return;
    }
    if (this.unauthoriseDonor) return;
    const qParam = `?with=activation;comments;productSales.campaignProduct.product&limit=${limit}&page=${page}&orderBy=${orderby}&sortedBy=${sortby}`;
    const url = API_ENDTPOINTS.GET_DONORS(this.fundraiser.custom_tag) + qParam;
    this.loading = true;
    this.unauthoriseDonor = false;
    this.api.get(url, 'false').subscribe({
      next: (res: any) => {
        this.loading = false;
        if (!res?.data?.data?.length) return;
        res.data.data = res.data.data.map(item => {
          item['cdn_image'] = item?.entity?.avtar?.cdn_path || item?.donoravtar?.cdn_path || item?.avtar?.cdn_path || '';
          if (item?.is_anonymous === 1) item['cdn_image'] = 'anonymous';
          return item;
        });
        this.pageData = res?.data;
        this.visibleSupporters = res.data.data;
        this.supportersCache.set(page, {
          supporters: this.visibleSupporters,
          from: this.pageData.from,
          to: this.pageData.to,
          next_page_url: this.pageData.next_page_url,
          prev_page_url: this.pageData.prev_page_url
        });
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.loading = false;
        if (err?.error?.message === 'Unauthorized access') {
          this.unauthoriseDonor = true;
        }
      }
    });
  }

  nextPage() {
    if (this.pageData && this.pageData.next_page_url) {
      this.getSupporters(this.pageData.current_page + 1);
    }
  }

  previousPage() {
    if (this.pageData && this.pageData.prev_page_url) {
      this.getSupporters(this.pageData.current_page - 1);
    }
  }

  findDonation() {
    this.closeSupporter();
    this.dialog.open(AcknowledgeFormComponent, {
      data: { mode: 'paytm' },
      width: '100vw',
      height: '100vh',
      maxWidth: '600px',
      maxHeight: this.isMobile ? '100vh' : '550px',
    });
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

}
